/**
 * Context bindings — list view on the shared list grammar (design-system §4.2):
 * the edges that connect a context to an artifact, package, rule, or actor.
 *
 * Both ends of the edge are shown by *name*, never a raw id (§5 rule 1): the
 * Context resolves via RefLink, and the bound entity resolves via RefLink against
 * the resource its `entity_type` maps to. The binding type and access mode are
 * calm chips; the shared DataTable supplies Loading / Empty / Error states.
 */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useGo, useNavigation } from "@refinedev/core";
import type { ContextBinding } from "@aos/shared";

import { Badge } from "@/components/ui/badge";
import { RefLink } from "@/components/inspector";

import { ResourceListPage, RowActions } from "../_shared/ListScaffold";
import { humanize } from "../projects/_form";

/** Map a binding `entity_type` to the Refine resource its `entity_id` links to. */
const ENTITY_TYPE_RESOURCE: Record<string, string> = {
  artifact: "artifacts",
  package: "packages",
  skill: "skills",
  contour: "processes",
  process: "processes",
  signal: "signals",
  rule: "rules",
  document: "documents",
  bet: "bets",
  frame: "frames",
  principle: "principles",
  metric: "metrics",
  decision: "decisions",
  objective: "objectives",
  key_result: "key-results",
  phase: "phases",
};

export function ContextBindingList() {
  const go = useGo();
  const { editUrl, showUrl } = useNavigation();
  const [pendingDelete, setPendingDelete] =
    React.useState<ContextBinding | null>(null);

  const columns = useMemo<ColumnDef<ContextBinding, unknown>[]>(
    () => [
      {
        id: "context_id",
        accessorKey: "context_id",
        header: "Context",
        enableSorting: false,
        cell: ({ row }) => (
          <RefLink resource="contexts" id={row.original.context_id} />
        ),
      },
      {
        id: "binding_type",
        accessorKey: "binding_type",
        header: "Binding",
        cell: ({ row }) => (
          <Badge variant="outline">{humanize(row.original.binding_type)}</Badge>
        ),
      },
      {
        id: "entity_id",
        accessorKey: "entity_id",
        header: "Bound entity",
        enableSorting: false,
        cell: ({ row }) => {
          const resource = ENTITY_TYPE_RESOURCE[row.original.entity_type];
          return (
            <div className="flex flex-col gap-0.5">
              {resource ? (
                <RefLink resource={resource} id={row.original.entity_id} />
              ) : (
                <span className="font-mono text-xs text-muted-foreground">
                  {row.original.entity_id}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                {humanize(row.original.entity_type)}
              </span>
            </div>
          );
        },
      },
      {
        id: "access_mode",
        accessorKey: "access_mode",
        header: "Access",
        cell: ({ row }) => (
          <Badge variant="secondary">{humanize(row.original.access_mode)}</Badge>
        ),
      },
      {
        id: "priority",
        accessorKey: "priority",
        header: "Priority",
        cell: ({ row }) => (
          <span className="tabular-nums text-muted-foreground">
            {row.original.priority}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() =>
              go({ to: showUrl("context-bindings", row.original.id) })
            }
            onEdit={() =>
              go({ to: editUrl("context-bindings", row.original.id) })
            }
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  return (
    <ResourceListPage<ContextBinding>
      resource="context-bindings"
      title="Context Bindings"
      description="Edges that connect contexts to artifacts, packages, rules, and actors."
      createLabel="New Binding"
      columns={columns}
      initialSort={[{ field: "priority", order: "desc" }]}
      searchPlaceholder="Search bindings…"
      emptyMessage="No context bindings yet. Bind a context to its first entity to get started."
      deleteEntityLabel="binding"
      pendingDelete={pendingDelete}
      setPendingDelete={setPendingDelete}
    />
  );
}

export default ContextBindingList;
